import type { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { ElementTestId } from 'enums/testing';

import { BORDER_RADIUS_200, SPACE_200 } from 'styles/tokens';

interface ButtonGroupProps {
  children: ReactNode;
  fullWidth?: boolean;
  segmented?: boolean;
}

const StyledButtonGroup = styled.div<{ $fullWidth?: boolean; $segmented?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  max-width: 100%;
  overflow-x: hidden;

  &:empty {
    display: none;
  }

  ${({ $fullWidth, $segmented }) => {
    if ($segmented) {
      return css`
        flex-wrap: nowrap;

        > * {
          /* Remove border-radius from middle buttons */
          border-radius: 0;
          flex: ${$fullWidth ? '1' : '0 0 auto'};
          margin-right: -1px;

          &:first-child {
            border-bottom-left-radius: ${BORDER_RADIUS_200};
            border-top-left-radius: ${BORDER_RADIUS_200};
          }

          &:last-child {
            border-bottom-right-radius: ${BORDER_RADIUS_200};
            border-top-right-radius: ${BORDER_RADIUS_200};
            margin-right: 0;
          }
        }
      `;
    }

    if ($fullWidth) {
      return css`
        gap: ${SPACE_200};

        > * {
          flex: 1;
        }
      `;
    }

    return css`
      gap: ${SPACE_200};
    `;
  }}
`;

const ButtonGroup = ({ children, fullWidth = false, segmented = false }: ButtonGroupProps) => (
  <StyledButtonGroup $fullWidth={fullWidth} $segmented={segmented} data-testid={ElementTestId.BUTTON_GROUP}>
    {children}
  </StyledButtonGroup>
);

export default ButtonGroup;
